import { App as AntApp, ConfigProvider, Flex, Grid, Menu, Spin } from 'antd';
import arEG from 'antd/locale/ar_EG';
import enUS from 'antd/locale/en_US';
import frFR from 'antd/locale/fr_FR';
import { lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { FileViewerProvider } from './context/FileViewer';
import { SiteProvider, useSiteContext } from './context/SiteContext';
import { RTL_LOCALES } from './i18n';
import { AppShell } from './layout/AppShell';
import { DashboardPage } from './pages/DashboardPage';
import { SimpleShell } from './layout/SimpleShell';
import { NoAccessPage } from './pages/NoAccessPage';
import { SuspendedPage } from './pages/SuspendedPage';
import { SignInPage } from './pages/SignInPage';
import { TwoStepRequiredPage } from './pages/TwoStepRequiredPage';
import { appTheme } from './theme';
import { useThemeMode } from './themeMode';

/** Pages load on first visit; the dashboard ships with the shell. */
const OrganisationPage = lazy(() =>
  import('./pages/admin/OrganisationPage').then((m) => ({ default: m.OrganisationPage })),
);
const ReturnsPage = lazy(() =>
  import('./pages/collecte/ReturnsPage').then((m) => ({ default: m.ReturnsPage })),
);
const UsersPage = lazy(() =>
  import('./pages/admin/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const AuditPage = lazy(() =>
  import('./pages/admin/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const CapsPage = lazy(() => import('./pages/caps/CapsPage').then((m) => ({ default: m.CapsPage })));
const OtherDocumentsPage = lazy(() =>
  import('./pages/documents/OtherDocuments').then((m) => ({ default: m.OtherDocumentsPage })),
);
const CmdSearchPage = lazy(() =>
  import('./pages/CmdSearchPage').then((m) => ({ default: m.CmdSearchPage })),
);
const GroupPage = lazy(() => import('./pages/GroupPage').then((m) => ({ default: m.GroupPage })));
const GroupRegistrePage = lazy(() =>
  import('./pages/GroupRegistrePage').then((m) => ({ default: m.GroupRegistrePage })),
);
const RefImpactsPage = lazy(() =>
  import('./pages/RefImpactsPage').then((m) => ({ default: m.RefImpactsPage })),
);
const ConditionnementsPage = lazy(() =>
  import('./pages/ConditionnementsPage').then((m) => ({ default: m.ConditionnementsPage })),
);
const PrestataireDetailPage = lazy(() =>
  import('./pages/prestataires/PrestataireDetailPage').then((m) => ({
    default: m.PrestataireDetailPage,
  })),
);
const PrestatairesPage = lazy(() =>
  import('./pages/prestataires/PrestatairesPage').then((m) => ({ default: m.PrestatairesPage })),
);
const PerformancePage = lazy(() =>
  import('./pages/prestataires/PerformancePage').then((m) => ({ default: m.PerformancePage })),
);
const OriginsPage = lazy(() =>
  import('./pages/settings/OriginsPage').then((m) => ({ default: m.OriginsPage })),
);
const AnalysesPage = lazy(() =>
  import('./pages/analyses/AnalysesPage').then((m) => ({ default: m.AnalysesPage })),
);
const PivotPage = lazy(() =>
  import('./pages/analyses/PivotPage').then((m) => ({ default: m.PivotPage })),
);
const InvoicesPage = lazy(() =>
  import('./pages/costs/InvoicesPage').then((m) => ({ default: m.InvoicesPage })),
);
const TransfersPage = lazy(() =>
  import('./pages/collecte/TransfersPage').then((m) => ({ default: m.TransfersPage })),
);
const ActivityPage = lazy(() =>
  import('./pages/settings/ActivityPage').then((m) => ({ default: m.ActivityPage })),
);
const SitePreferencesPage = lazy(() =>
  import('./pages/settings/SitePreferencesPage').then((m) => ({ default: m.SitePreferencesPage })),
);
const WasteDetailPage = lazy(() =>
  import('./pages/wastes/WasteDetailPage').then((m) => ({ default: m.WasteDetailPage })),
);
const ExpeditionsPage = lazy(() =>
  import('./pages/expeditions/ExpeditionsPage').then((m) => ({ default: m.ExpeditionsPage })),
);
const ChargementPage = lazy(() =>
  import('./pages/chargements/ChargementPage').then((m) => ({ default: m.ChargementPage })),
);
const PickupRequestsPage = lazy(() =>
  import('./pages/collecte/PickupRequestsPage').then((m) => ({ default: m.PickupRequestsPage })),
);
const StockPage = lazy(() =>
  import('./pages/collecte/StockPage').then((m) => ({ default: m.StockPage })),
);
const VerifyPage = lazy(() =>
  import('./pages/VerifyPage').then((m) => ({ default: m.VerifyPage })),
);
const PrivacyPage = lazy(() =>
  import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);
const AnnualReportPage = lazy(() =>
  import('./pages/collecte/AnnualReportPage').then((m) => ({ default: m.AnnualReportPage })),
);
const RegistrePage = lazy(() =>
  import('./pages/collecte/RegistrePage').then((m) => ({ default: m.RegistrePage })),
);
const ImportsPage = lazy(() =>
  import('./pages/imports/ImportsPage').then((m) => ({ default: m.ImportsPage })),
);
const ImportBatchPage = lazy(() =>
  import('./pages/imports/ImportBatchPage').then((m) => ({ default: m.ImportBatchPage })),
);
const RoutesPage = lazy(() =>
  import('./pages/wastes/RoutesPage').then((m) => ({ default: m.RoutesPage })),
);
const WastesPage = lazy(() =>
  import('./pages/wastes/WastesPage').then((m) => ({ default: m.WastesPage })),
);
const CompliancePage = lazy(() =>
  import('./pages/collecte/CompliancePage').then((m) => ({ default: m.CompliancePage })),
);
const CalendarPage = lazy(() =>
  import('./pages/collecte/CalendarPage').then((m) => ({ default: m.CalendarPage })),
);
const IndicatorsPage = lazy(() =>
  import('./pages/collecte/IndicatorsPage').then((m) => ({ default: m.IndicatorsPage })),
);
const ContractsPage = lazy(() =>
  import('./pages/costs/ContractsPage').then((m) => ({ default: m.ContractsPage })),
);
const ClientsPage = lazy(() =>
  import('./pages/platform/ClientsPage').then((m) => ({ default: m.ClientsPage })),
);
const ClientPage = lazy(() =>
  import('./pages/platform/ClientPage').then((m) => ({ default: m.ClientPage })),
);
const BulletinsPage = lazy(() =>
  import('./pages/platform/BulletinsPage').then((m) => ({ default: m.BulletinsPage })),
);
const FirstRunPage = lazy(() =>
  import('./pages/onboarding/FirstRunPage').then((m) => ({ default: m.FirstRunPage })),
);

const antLocales = { fr: frFR, ar: arEG, en: enUS } as const;

function FullScreenSpin() {
  return (
    <Flex align="center" justify="center" style={{ height: '100vh' }}>
      <Spin size="large" />
    </Flex>
  );
}

/** The sections of the vendor console, always in view; its name when there is room. */
function PlatformNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  return (
    <Flex align="center" gap={16} style={{ minWidth: 0, width: '100%' }}>
      {screens.lg && <span>{t('clients.console')}</span>}
      <Menu
        mode="horizontal"
        disabledOverflow
        selectedKeys={[pathname.startsWith('/bulletins') ? '/bulletins' : '/']}
        onClick={({ key }) => navigate(key)}
        items={[
          { key: '/', label: t('clients.title') },
          { key: '/bulletins', label: t('bulletinsAdmin.nav') },
        ]}
        style={{ flex: 1, minWidth: 0, borderBottom: 'none', background: 'transparent' }}
      />
    </Flex>
  );
}

/** The vendor console: the clients of the platform and the bulletins (platform operators). */
function PlatformApp() {
  return (
    <SimpleShell context={<PlatformNav />}>
      <Routes>
        <Route index element={<ClientsPage />} />
        <Route path="clients/:id" element={<ClientPage />} />
        <Route path="bulletins" element={<BulletinsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SimpleShell>
  );
}

function AuthenticatedApp() {
  const { loading, sites, can, error, me } = useSiteContext();
  if (loading) return <FullScreenSpin />;
  // an administrator's session without the second factor (ADR-0006)
  if (me?.principal.twoStep?.satisfied === false) return <TwoStepRequiredPage />;
  if (!error && me?.principal.platformOperator) return <PlatformApp />;
  // a client the vendor suspended: who they are, and its administrators' data exports
  if (!error && me?.tenant.status === 'SUSPENDED') return <SuspendedPage />;
  // a new client: its administrator creates the first company and site
  if (!error && sites.length === 0 && me?.principal.tenantRoles.includes('TENANT_ADMIN')) {
    return (
      <Suspense fallback={<FullScreenSpin />}>
        <FirstRunPage />
      </Suspense>
    );
  }
  if (error || sites.length === 0) return <NoAccessPage />;
  const canImport =
    can.configureSite ||
    can.administerSite ||
    can.administerTenant ||
    can.administerUsers ||
    can.manageCosts ||
    can.createOperations;
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<DashboardPage />} />
        {can.group && <Route path="group" element={<GroupPage />} />}
        {can.group && <Route path="group/registre" element={<GroupRegistrePage />} />}
        {can.group && <Route path="group/analyses" element={<AnalysesPage scope="group" />} />}
        {can.group && <Route path="group/analyses/pivot" element={<PivotPage scope="group" />} />}
        <Route path="pickup-requests" element={<PickupRequestsPage />} />
        <Route path="expeditions" element={<ExpeditionsPage />} />
        <Route path="returns" element={<ReturnsPage />} />
        <Route path="transfers" element={<TransfersPage />} />
        <Route path="chargements/new" element={<ChargementPage />} />
        <Route path="chargements/:id" element={<ChargementPage />} />
        <Route path="stock" element={<StockPage />} />
        <Route path="registre" element={<RegistrePage />} />
        <Route path="annual-report" element={<AnnualReportPage />} />
        <Route path="compliance" element={<CompliancePage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="indicators" element={<IndicatorsPage />} />
        <Route path="analyses" element={<AnalysesPage scope="site" />} />
        <Route path="analyses/pivot" element={<PivotPage scope="site" />} />
        <Route path="wastes" element={<WastesPage />} />
        <Route path="wastes/:id" element={<WasteDetailPage />} />
        <Route path="filieres" element={<RoutesPage />} />
        <Route path="conditionnements" element={<ConditionnementsPage />} />
        <Route path="prestataires" element={<PrestatairesPage />} />
        <Route path="prestataires/performance" element={<PerformancePage />} />
        <Route path="prestataires/:id" element={<PrestataireDetailPage />} />
        <Route path="caps" element={<CapsPage />} />
        <Route path="other-documents" element={<OtherDocumentsPage />} />
        {can.contracts && <Route path="contracts" element={<ContractsPage />} />}
        {can.contracts && <Route path="invoices" element={<InvoicesPage />} />}
        {canImport && <Route path="imports" element={<ImportsPage />} />}
        {canImport && <Route path="imports/:id" element={<ImportBatchPage />} />}
        <Route path="settings/site" element={<SitePreferencesPage />} />
        <Route path="settings/origins" element={<OriginsPage />} />
        <Route path="settings/activity" element={<ActivityPage />} />
        <Route path="ref/cmd" element={<CmdSearchPage />} />
        <Route path="ref/impacts" element={<RefImpactsPage />} />
        {can.administerTenant && <Route path="admin/organisation" element={<OrganisationPage />} />}
        {can.administerUsers && <Route path="admin/users" element={<UsersPage />} />}
        {can.administerUsers && <Route path="admin/audit" element={<AuditPage />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  const { i18n } = useTranslation();
  const { user, loading } = useAuth();
  // the verification page of a printed document (FR-FIA-15) and the privacy notice (loi 09-08)
  // open without signing in
  const pathname = useLocation().pathname;
  const publicPage = pathname.startsWith('/verify/') || pathname === '/privacy';
  const locale = (i18n.language.slice(0, 2) as keyof typeof antLocales) ?? 'fr';
  const { mode } = useThemeMode();
  const antTheme = useMemo(() => appTheme(mode), [mode]);
  return (
    <ConfigProvider
      theme={antTheme}
      locale={antLocales[locale] ?? frFR}
      direction={RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr'}
      form={{ requiredMark: 'optional' }}
    >
      <AntApp>
        {publicPage ? (
          <Suspense fallback={<FullScreenSpin />}>
            <Routes>
              <Route path="/verify/:id" element={<VerifyPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
            </Routes>
          </Suspense>
        ) : loading ? (
          <FullScreenSpin />
        ) : !user ? (
          <SignInPage />
        ) : (
          <SiteProvider>
            <FileViewerProvider>
              <AuthenticatedApp />
            </FileViewerProvider>
          </SiteProvider>
        )}
      </AntApp>
    </ConfigProvider>
  );
}
